import { submitFormJson } from 'widgets/toolbox/ajax';

/**
 * @typedef {InstanceType<typeof import('widgets/toolbox/RefElement').RefElement>} RefElement
 * @typedef {ReturnType<typeof import('widgets/forms/AjaxForm').default>} AjaxForm
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/BasicInput').default>>} basicInput
 * @typedef {InstanceType<ReturnType<typeof import('widgets/global/Button').default>>} button
 */

/**
 * @description Base CouponForm implementation
 * @param {AjaxForm} AjaxForm Base widget for extending
 * @returns {typeof CouponForm} Coupon Form class
 */
export default function (AjaxForm) {
    /**
     * @category widgets
     * @subcategory forms
     * @class CouponForm
     * @augments AjaxForm
     * @classdesc Cart page promo code application AJAX form.
     * Used to apply/delete coupons for current cart.
     * Widget also communicates application results (either success or error) to customer.
     * When coupon application form is opened, sets focus on coupon input.
     * @property {string} data-widget - Widget name `couponform`
     * @property {string} data-error-message-label - Specific error message label
     * @property {string} data-event-submit - Event listener for submit form event
     * @example <caption>Example of CouponForm widget markup</caption>
     * <form
     *     ...
     *     method="POST"
     *     data-widget="couponform"
     *     data-remove-coupon-url="${pdict.actionUrls.removeCouponLineItem}"
     *     data-id="couponForm"
     *     data-error-message-label="errorFeedback"
     *     data-event-submit.prevent="handleSubmit"
     * >
     *     <isprint value="${
     *         formElement(require('server').forms.getForm('coupon').couponCode).render()
     *     }" encoding="off"/>
     *     <button
     *         type="submit"
     *         class="b-button m-outline"
     *         data-widget="button"
     *         data-widget-event-click="handleSubmit"
     *         data-event-click.prevent="handleClick"
     *         data-id="submitButton"
     *         data-tau="coupon_submit"
     *     >
     *         ${Resource.msg('form.promoCode.submit', 'forms', null)}
     *     </button>
     *     <div class="b-coupon_form-error" data-ref="couponDeletionMsg" role="alert" hidden></div>
     *     <!-- below is the list of applied coupons -->
     *     <isinclude template="m/cart/cartCouponDisplay" />
     * </form>
     */
    class CouponForm extends AjaxForm {
        prefs() {
            return {
                msgEmptyCoupon: '',
                couponInput: 'dwfrm_coupon_couponCode',
                couponCodeContent: 'couponCodeContent',
                couponDeletionMsg: 'couponDeletionMsg',
                removeCouponUrl: '',
                classesShow: 'show',
                ...super.prefs()
            };
        }

        /**
         * @description `onSubmitted` handler - communicates either error or success coupon application status
         * Notifies {@link CartMgr} about coupon status changes
         * @param {object} data server JSON response
         * @emits "coupon.updated"
         * @returns {void}
         */
        onSubmitted(data) {
            if (!data) {
                return;
            }
            const params = window.location.search && new URLSearchParams(window.location.search);
            const pageViewData = document.getElementById('pageViewData');
            const isBOPIS = document.getElementById('bopis-flag');
            const customerInfo = pageViewData?.getAttribute('data-gtm');
            var isLoggedIn;
            if (customerInfo) {
                isLoggedIn = JSON.parse(customerInfo).customerLoggedIn;
            }

            if (!isLoggedIn || isBOPIS?.value
                || (params && params.has('step') && (params.get('step') === 'shipping' || params.get('step') === 'billing')
                && data.totals && (data.totals.isZeroTotal || data.totals.grandTotal === '$0.00'))) {
                window.location.reload();
            }

            if (data.error && data.errorMessage) {
                this.getById(this.prefs().couponInput, (/** @type {basicInput} */ input) => input.setError(data.errorMessage));
            } else if (data.error && data.fieldErrors) {
                Object.keys(data.fieldErrors).forEach(errorKey => {
                    this.getById(errorKey, (/** @type {basicInput} */ input) => input.setError(data.fieldErrors[errorKey]));
                });
            } else {
                this.getById(this.prefs().couponInput, (/** @type {basicInput} */ input) => input.setValue(''));
                /**
                 * @description Event to notify concerned widgets, that coupon was updated
                 * @event "coupon.updated"
                 */
                this.eventBus().emit('coupon.updated', data);
            }
        }

        /**
         * @description Invalidates coupon code input with relevant error message
         * @param {Error} error - error, happened during request
         * @returns {void}
         */
        onError(error) {
            this.getById(this.prefs().couponInput, (/** @type {basicInput} */ input) => input.setError(error.message));
        }

        /**
         * @description Removes coupon code from cart
         * @param {button} button - triggering button
         * @emits "coupon.updated"
         * @returns {void}
         */
        removeCoupon(button) {
            this.showProgressBar();
            this.has(this.prefs().couponDeletionMsg, function (errMsg) {
                errMsg.setText('').hide();
            });

            submitFormJson(this.prefs().removeCouponUrl, {
                couponCode: button.data('code'),
                uuid: button.data('uuid')
            })
                .then((response) => {
                    response.couponAction = 'removed';
                    this.eventBus().emit('coupon.updated', response);
                    const params = window.location.search && new URLSearchParams(window.location.search);
                    const pageViewData = document.getElementById('pageViewData');
                    const isBOPIS = document.getElementById('bopis-flag');
                    const customerInfo = pageViewData?.getAttribute('data-gtm');
                    var isLoggedIn;
                    if (customerInfo) {
                        isLoggedIn = JSON.parse(customerInfo).customerLoggedIn;
                    }

                    if (!isLoggedIn || isBOPIS?.value
                        || (params && params.has('step')
                        && (params.get('step') === 'shipping' || params.get('step') === 'billing')
                        && response.isZeroDollarCouponRemoved)) {
                        window.location.reload();
                    }
                })
                .catch((err) => {
                    this.has(this.prefs().couponDeletionMsg, function (errMsg) {
                        errMsg.setText(err.message).show();
                    });
                })
                .finally(() => {
                    this.hideProgressBar();
                });
        }

        /**
         * @description Sets focus on coupon input. Usually happens when coupon form is opened.
         * @returns {void}
         */
        focusInput() {
            this.getById(this.prefs().couponInput, (/** @type {basicInput} */ input) => input.focus());
        }
    }

    return CouponForm;
}
